/**
 * In-house product analytics SDK (client side).
 *
 * Batches PHI-free events and POSTs them to the backend `/analytics/collect`
 * endpoint, which stamps identity server-side. Deliberately uses a RAW `fetch`
 * (never the shared `api` axios instance) so a 401/expiry can NEVER trip the
 * auth interceptor and bounce the user to login (documented INV-20 exception).
 * Best-effort telemetry: every failure is swallowed, and the queue is dropped
 * if there is no auth token (INV-19 telemetry exemption — never a clinical path).
 */
import { getAuth } from 'firebase/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';
const ENABLED = process.env.REACT_APP_INHOUSE_ANALYTICS !== 'false';
const APP_VERSION = process.env.REACT_APP_VERSION || undefined;

const MAX_BATCH = 20;
const FLUSH_MS = 15_000;
const SID_KEY = 'st_analytics_sid';

interface QueuedEvent {
  event: string;
  category?: string;
  surface?: string;
  sessionId?: string;
  occurredAt: string;
  properties?: Record<string, unknown>;
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let started = false;

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const LONG_NUM_SEGMENT_RE = /\/\d{2,}(?=\/|$)/g;

/** De-identify a pathname: uuid + long-numeric segments → `:id`. pathname only — never href/search. */
export function sanitizePath(pathname: string): string {
  if (!pathname) return '/';
  const path = pathname.split('?')[0].split('#')[0] || '/';
  return path.replace(UUID_RE, ':id').replace(LONG_NUM_SEGMENT_RE, '/:id');
}

function firstSegment(pathname: string): string {
  const seg = sanitizePath(pathname).split('/').filter(Boolean)[0];
  return seg ?? 'home';
}

function sessionId(): string {
  try {
    let sid = sessionStorage.getItem(SID_KEY);
    if (!sid) {
      sid =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    return '00000000-0000-0000-0000-000000000000';
  }
}

async function getToken(): Promise<string | null> {
  if (DEV_BYPASS) return 'dev-bypass-token';
  try {
    // Resolve the default-app auth lazily so importing this SDK never triggers
    // Firebase init (keeps it side-effect-free for tests / non-Firebase envs).
    const user = getAuth().currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch {
    return null;
  }
}

async function flush(keepalive = false): Promise<void> {
  if (queue.length === 0) return;
  const token = await getToken();
  if (!token) {
    // No identity to attribute to — drop rather than grow unbounded.
    queue = [];
    return;
  }
  const events = queue.splice(0, MAX_BATCH);
  try {
    await fetch(`${API_BASE}/analytics/collect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ appVersion: APP_VERSION, events }),
      keepalive,
    });
  } catch {
    // Best-effort: never surface, never retry.
  }
}

/** Enqueue one event. Safe to call anywhere — non-blocking, never throws. */
export function trackInHouse(
  event: string,
  properties?: Record<string, unknown>,
  opts?: { category?: string; surface?: string },
): void {
  if (!ENABLED) return;
  try {
    queue.push({
      event,
      category: opts?.category,
      surface: opts?.surface,
      sessionId: sessionId(),
      occurredAt: new Date().toISOString(),
      properties,
    });
    if (queue.length >= MAX_BATCH) void flush();
  } catch {
    // ignore
  }
}

let lastPageView: string | null = null;

/** Emit a page_view for a route change (path is de-identified; de-duplicated). */
export function trackInHousePageView(pathname: string): void {
  const path = sanitizePath(pathname);
  if (path === lastPageView) return;
  lastPageView = path;
  trackInHouse('page_view', { path }, { category: 'nav', surface: firstSegment(pathname) });
}

/** Bind the flush timer + unload beacons once. Idempotent. */
export function startInHouseAnalytics(): void {
  if (started || !ENABLED || typeof window === 'undefined') return;
  started = true;
  timer = setInterval(() => void flush(), FLUSH_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
  window.addEventListener('pagehide', () => void flush(true));
}

/** For tests only. */
export function __resetInHouseForTest(): void {
  queue = [];
  started = false;
  if (timer) clearInterval(timer);
  timer = null;
}
